import { create } from 'zustand';
import type { ViewType } from '@/config/constants';
import type { ThemeMode } from '@/types/common';
import type { CRMField, CRMRecord } from '@/types/saas';
import type { Conversation, Message, Recipient, Sender } from '@/types/messaging';
import { getStorageItem, setStorageItem } from '@/lib/utils/storage';
import { useModuleStore } from '@/stores/module-store';

/** A phone-type field from the CRM record (e.g. Phone, Mobile, Other_Phone) */
export interface CrmPhoneField {
  apiName: string;
  displayLabel: string;
  value: string; // The actual phone number from the record
  /** Unique key for this entry (apiName for single record, or recordId_apiName for multi) */
  key?: string;
  /** The CRM record's display name this field belongs to (for multi-record context) */
  recordName?: string;
}

interface UIStore {
  activeView: ViewType;
  sidebarOpen: boolean;
  theme: ThemeMode;
  commandPaletteOpen: boolean;
  activeSettingsTab: string | null;
  activeModal: string | null;
  toastQueue: ToastItem[];

  /** True once app-init has determined the correct default view + CRM context.
   *  Until resolved, the header shows a skeleton instead of nav tabs. */
  viewResolved: boolean;

  /** True when app is loaded in CRM record context (module + entityId present).
   *  In this mode, CHAT view shows only the chatbox — no conversation sidebar. */
  crmRecordContext: boolean;

  /** Available phone fields from the CRM record (populated during init) */
  crmPhoneFields: CrmPhoneField[];
  /** Currently selected phone field API name (e.g. "Phone", "Mobile") */
  crmSelectedPhoneField: string | null;
  /** CRM module field definitions — used to filter placeholder fields by data type */
  crmModuleFields: CRMField[];
  /** Records added through the compose "Search CRM" dialog — feed the
   *  recipient picker's phone-field pills alongside crmPhoneFields. */
  crmSearchRecords: CRMRecord[];
  /** The rows the user ticked in that dialog (before the one-per-number
   *  dedup) — only these numbers are pill-controlled, never the record's
   *  other, unticked numbers. */
  crmSearchRecipients: Recipient[];

  /** True while CrmChatView is searching Twilio for recent conversations.
   *  CrmContextBar checks this to defer its auto-construct until the search completes. */
  crmTwilioSearching: boolean;

  /**
   * All conversations resolved for the current CRM record (primary + others),
   * with their pre-fetched messages and matched sender. Populated by
   * CrmChatView after `searchContactMessages` succeeds.
   *
   * Used by CrmContextBar to render a "Conversation" picker for non-phone
   * TPAs (e.g. Intercom) where one CRM record maps to many conversations
   * across multiple TPA-side contacts. Phone-based TPAs read it too (the
   * "Other conversations" banner uses the same data) but the picker is
   * gated on `tpaModule.isPhoneBasedRecipient() === false`.
   */
  crmRecordConversations: Array<{
    conversation: Conversation;
    messages: Message[];
    sender: Sender;
  }>;

  /** Whether the CRM lookup panel is visible in conversation view */
  lookupPanelOpen: boolean;

  /** Previous view before navigating to SETTINGS (so we can close back).
   *  Kept for backwards compatibility with any place that still reads it,
   *  but the settings overlay no longer needs it — `activeView` stays on
   *  the underlying view while Settings is open. */
  previousView: ViewType | null;

  /** Whether the Settings overlay is currently open. Settings renders as an
   *  overlay on top of `activeView`, not as a view swap — so opening /
   *  closing Settings never unmounts the inbox/chat/form subtree and no
   *  refetch happens on close. */
  settingsOpen: boolean;
  openSettings(): void;
  closeSettings(): void;

  /** Title of the currently open settings detail panel (null = grid view) */
  settingsDetailTitle: string | null;

  /** True when background check detects incoming sync is not configured */
  incomingSyncAlert: boolean;
  setIncomingSyncAlert(show: boolean): void;

  /** Populated by a background check when the SaaS org the user authorized
   *  (`getCurrentOrg`) differs from the org the current iframe is actually
   *  embedded in (e.g. Zoho `getOrgInfo`). Renders a full-page overlay
   *  prompting re-authentication because every API call would otherwise
   *  read/write records in the wrong account. */
  orgMismatch: {
    saasId: string;
    authorizedOrgId: string;
    authorizedOrgName?: string;
    currentOrgId: string;
  } | null;
  setOrgMismatch(info: UIStore['orgMismatch']): void;

  /** Conversation id currently shown in the floating ChatDialog (null = closed).
   *  Set by clicking a desktop notification, the chat-icon on a notification
   *  panel item, or by the `?openChatId=` URL param on app load. */
  chatDialogConversationId: string | null;
  openChatDialog(conversationId: string): void;
  closeChatDialog(): void;

  setView(view: ViewType): void;
  setViewResolved(resolved: boolean): void;
  setCrmRecordContext(active: boolean): void;
  setCrmPhoneFields(fields: CrmPhoneField[]): void;
  setCrmSelectedPhoneField(apiName: string): void;
  setCrmModuleFields(fields: CRMField[]): void;
  addCrmSearchRecords(records: CRMRecord[], recipients: Recipient[]): void;
  clearCrmSearchRecords(): void;
  setCrmTwilioSearching(searching: boolean): void;
  setCrmRecordConversations(
    entries: Array<{ conversation: Conversation; messages: Message[]; sender: Sender }>
  ): void;
  toggleLookupPanel(): void;
  setLookupPanelOpen(open: boolean): void;
  toggleSidebar(): void;
  setSidebarOpen(open: boolean): void;
  toggleTheme(): void;
  setTheme(theme: ThemeMode): void;
  toggleCommandPalette(): void;
  setActiveSettingsTab(tab: string | null): void;
  setSettingsDetailTitle(title: string | null): void;
  openModal(id: string): void;
  closeModal(): void;
  addToast(toast: Omit<ToastItem, 'id'>): void;
  removeToast(id: string): void;
}

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

export const useUIStore = create<UIStore>((set, get) => ({
  activeView: 'MESSAGE_FORM',
  viewResolved: false,
  crmRecordContext: false,
  crmPhoneFields: [],
  crmSelectedPhoneField: null,
  crmModuleFields: [],
  crmSearchRecords: [],
  crmSearchRecipients: [],
  crmTwilioSearching: false,
  crmRecordConversations: [],
  previousView: null,
  settingsOpen: false,
  settingsDetailTitle: null,
  incomingSyncAlert: false,
  setIncomingSyncAlert: (show) => set({ incomingSyncAlert: show }),

  orgMismatch: null,
  setOrgMismatch: (info) => set({ orgMismatch: info }),

  chatDialogConversationId: null,
  openChatDialog: (conversationId) => set({ chatDialogConversationId: conversationId }),
  closeChatDialog: () => set({ chatDialogConversationId: null }),
  // `lookupPanelOpen` is deliberately NOT persisted. The persistent preference
  // is `lookup-store.showByDefault` — that drives whether the panel auto-opens
  // on conversation select. Persisting this flag caused the panel to reopen
  // on reload even when the user had turned `showByDefault` off.
  lookupPanelOpen: false,
  sidebarOpen: getStorageItem('sidebar_open', true),
  theme: getStorageItem<ThemeMode>('theme', 'light'),
  commandPaletteOpen: false,
  activeSettingsTab: null,
  activeModal: null,
  toastQueue: [],

  setView: (view) => {
    // Settings is an overlay, not a view swap. Opening Settings flips the
    // `settingsOpen` flag while leaving `activeView` on whatever the user
    // was actually looking at — so the inbox/chat/form subtree stays
    // mounted behind the overlay and closing Settings doesn't re-mount it
    // (no lost scroll, no refetch, no "which view do we restore to?" logic).
    if (view === 'SETTINGS') {
      set({ settingsOpen: true });
    } else {
      // Any non-Settings navigation also dismisses Settings if it was open.
      set({ activeView: view, settingsOpen: false });
    }
    // Sync URL so reload preserves the current view.
    try {
      const path = window.location.pathname;
      const KNOWN_VIEWS = new Set(['inbox', 'compose', 'settings', 'calls', 'voicemail', 'recordings', 'call-queues', 'schedule', 'history', 'links']);

      // 3-segment sub-view: /{appCode}/inbox/kanban (or legacy /inbox/{appCode}/kanban)
      const subViewMatch = path.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/?$/);
      // Universal format: /{appCode}/{viewPath} or legacy /{viewPath}/{appCode}
      const universalMatch = path.match(/^\/([^/]+)\/([^/]+)\/?$/);

      // Detect appCode across both formats
      let appName: string | null = null;
      let isCallRoute = false;

      if (subViewMatch) {
        const [, first, second, third] = subViewMatch;
        // /{appCode}/inbox/kanban
        if (second === 'inbox' && third === 'kanban') appName = first;
        // /inbox/{appCode}/kanban
        else if (first === 'inbox' && third === 'kanban') appName = second;
      } else if (universalMatch) {
        const [, first, second] = universalMatch;
        const isUniversal = KNOWN_VIEWS.has(second); // /{appCode}/{viewPath}
        const isLegacy = KNOWN_VIEWS.has(first);     // /{viewPath}/{appCode}
        appName = isUniversal ? first : isLegacy ? second : first;
        isCallRoute = ['calls', 'voicemail', 'recordings', 'call-queues'].includes(isUniversal ? second : first);
      }

      if (appName) {
        const viewToPath: Record<string, string> = {
          CONVERSATIONS: isCallRoute ? 'calls' : 'inbox',
          KANBAN: 'inbox/kanban',
          MESSAGE_FORM: 'compose',
          SETTINGS: 'settings',
          EVENTS: 'schedule',
        };
        const newViewPath = viewToPath[view];
        if (newViewPath) {
          // Preserve existing query string + hash so loading
          // `/inbox#<convId>?foo=bar` through setView doesn't strip them.
          const search = window.location.search;
          const hash = window.location.hash;
          const newUrl = `/${appName}/${newViewPath}${search}${hash}`;
          const currentFull = `${path}${search}${hash}`;
          if (newUrl !== currentFull) {
            window.history.replaceState(null, '', newUrl);
          }
        }
      } else {
        // Query-param based route. On single-view routes (e.g. /ai-actions)
        // only SETTINGS is a meaningful `force-ui-view` value — the route
        // has no MESSAGE_FORM / CONVERSATIONS to switch to. Writing a
        // non-SETTINGS view pollutes the URL bar (use-app-init strips it on
        // next load anyway). But we MUST still write SETTINGS so deep
        // links / open-in-new-tab can land on the settings overlay.
        const url = new URL(window.location.href);
        const isSingleViewRoute = url.pathname === '/ai-actions';
        if (isSingleViewRoute && view !== 'SETTINGS') {
          if (url.searchParams.has('force-ui-view')) {
            url.searchParams.delete('force-ui-view');
            window.history.replaceState(null, '', url.toString());
          }
        } else {
          url.searchParams.set('force-ui-view', view);
          window.history.replaceState(null, '', url.toString());
        }
      }
    } catch {
      // Ignore URL update failures
    }

    // Notify the CRM module so it can resize the iframe for the new view
    useModuleStore.getState().saasModule?.resizeForView?.(view);
  },
  setViewResolved: (resolved) => set({ viewResolved: resolved }),

  // Settings overlay actions. `openSettings` delegates to `setView('SETTINGS')`
  // for URL sync (keeps `/settings` working for bookmarks + reload).
  // `closeSettings` just flips the flag; the underlying `activeView` and its
  // subtree stay mounted the whole time, so nothing refetches.
  openSettings: () => {
    get().setView('SETTINGS');
  },
  closeSettings: () => {
    set({ settingsOpen: false, settingsDetailTitle: null });
    // Drop the settings sub-card hash (e.g. `#knowledge-base`). settings-view
    // writes it while a detail card is open, but its cleanup effect doesn't
    // fire before SettingsView unmounts on close — so without this the hash
    // would leak onto the underlying view's URL and re-open the same
    // sub-card the next time Settings is launched.
    try {
      if (window.location.hash) {
        const url = new URL(window.location.href);
        url.hash = '';
        window.history.replaceState(null, '', url.toString());
      }
    } catch {
      // Ignore URL update failures
    }
    // Re-sync the URL to the underlying `activeView` so a reload doesn't
    // reopen Settings. Delegate through setView for consistent URL handling.
    get().setView(get().activeView);
  },
  setCrmRecordContext: (active) => set({ crmRecordContext: active }),
  setCrmPhoneFields: (fields) => set({ crmPhoneFields: fields }),
  setCrmSelectedPhoneField: (apiName) => set({ crmSelectedPhoneField: apiName }),
  setCrmModuleFields: (fields) => set({ crmModuleFields: fields }),
  addCrmSearchRecords: (records, recipients) => {
    const keyOf = (r: CRMRecord) => `${r.module}_${r.id}`;
    const incoming = new Set(records.map(keyOf));
    const incomingIds = new Set(recipients.map((r) => r.id));
    set({
      crmSearchRecords: [...get().crmSearchRecords.filter((r) => !incoming.has(keyOf(r))), ...records],
      crmSearchRecipients: [...get().crmSearchRecipients.filter((r) => !incomingIds.has(r.id)), ...recipients],
    });
  },
  clearCrmSearchRecords: () => set({ crmSearchRecords: [], crmSearchRecipients: [] }),
  setCrmTwilioSearching: (searching) => set({ crmTwilioSearching: searching }),
  setCrmRecordConversations: (entries) => set({ crmRecordConversations: entries }),

  // `lookupPanelOpen` is ephemeral per-session state — no localStorage write.
  // The persistent preference lives in `lookup-store.showByDefault`.
  toggleLookupPanel: () => set({ lookupPanelOpen: !get().lookupPanelOpen }),
  setLookupPanelOpen: (open) => set({ lookupPanelOpen: open }),

  toggleSidebar: () => {
    const newState = !get().sidebarOpen;
    setStorageItem('sidebar_open', newState);
    set({ sidebarOpen: newState });
  },

  setSidebarOpen: (open) => {
    setStorageItem('sidebar_open', open);
    set({ sidebarOpen: open });
  },

  toggleTheme: () => {
    const newTheme = get().theme === 'light' ? 'dark' : 'light';
    setStorageItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
    set({ theme: newTheme });
  },

  setTheme: (theme) => {
    setStorageItem('theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
    set({ theme });
  },

  toggleCommandPalette: () =>
    set({ commandPaletteOpen: !get().commandPaletteOpen }),

  setActiveSettingsTab: (tab) => set({ activeSettingsTab: tab }),
  setSettingsDetailTitle: (title) => set({ settingsDetailTitle: title }),

  openModal: (id) => set({ activeModal: id }),
  closeModal: () => set({ activeModal: null }),

  addToast: (toast) => {
    const id = `toast_${Date.now()}`;
    set({ toastQueue: [...get().toastQueue, { ...toast, id }] });
    // Auto-remove after 5 seconds
    setTimeout(() => get().removeToast(id), 5000);
  },

  removeToast: (id) =>
    set({ toastQueue: get().toastQueue.filter((t) => t.id !== id) }),
}));

// Expose store for E2E testing — lets Playwright drive view state directly
// (e.g. flip to the STRIPE / GOOGLEDRIVE view from the tour without depending
// on app-init's route-handling branches). Mirrors the same DEV-only exposure
// as __moduleStore and __settingsStore.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__uiStore = useUIStore;
}
